import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";

export interface ISMTPConfiguration {
  server: string;
  port: number;
  username?: string;
  password?: string;
  useSSL: boolean;
}

export interface IShift {
  name: string;
  startTime: string;
  endTime: string;
}

export interface IConfiguration {
  key: string;
  value: string;
}

export interface ITimeConfiguration {
  timezone: string;
  dayStartOffset: number;
  dateTimeFormat: string;
  shifts: IShift[];
}

export interface ILandingPage {
  page: "home" | "dashboard";
  dashboardId?: string;
}

const endpoint = "/api/configuration";

export default function useConfiguration<T>(key: string) {
  const toast = useToast({
    status: "success",
    duration: 2000,
    isClosable: true,
    position: "top",
  });

  const queryClient = useQueryClient();

  const configuration = useQuery<T, AxiosError<any>>({
    queryKey: ["configuration", key],
    queryFn: async () => {
      const { data } = await axios.get<T>(`${endpoint}/${key}`);
      return data;
    },
  });

  const getAll = useQuery<IConfiguration[], AxiosError<any>>({
    queryKey: ["configurations"],
    queryFn: async () => {
      const { data } = await axios.get<IConfiguration[]>(endpoint);
      return data;
    },
  });

  const update = useMutation<T, AxiosError<any>, T, any>({
    mutationFn: async (config: T) => {
      const { data } = await axios.put(`${endpoint}/${key}`, config);
      return data;
    },
    onMutate: () => {
      toast.close(`update-toast-configuration-${key}`);
    },
    onSuccess: (_, updatedEntity) => {
      !toast.isActive(`update-toast-configuration-${key}`) &&
        toast({
          title: "Configuration updated",
          id: `update-toast-configuration-${key}`,
        });

      queryClient.setQueryData(["configuration", key], updatedEntity);
    },
    onError: (error: AxiosError<any>) => {
      toast({
        title: error?.response?.data ?? "Error",
        status: "error",
        id: `update-toast-configuration-${key}`,
      });
    },
  });

  const updateConfig = useMutation<
    string,
    AxiosError<any>,
    IConfiguration,
    any
  >({
    mutationFn: async (config: IConfiguration) => {
      const { data } = await axios.put(`${endpoint}/${config.key}/config`, {
        value: config.value,
      });
      return data;
    },
    onMutate: () => {
      toast.close(`update-toast-configuration-${key}`);
    },
    onSuccess: (_, updatedEntity) => {
      !toast.isActive(`update-toast-configuration-${updatedEntity.key}`) &&
        toast({
          title: "Configuration updated",
          id: `update-toast-configuration-${key}`,
        });

      queryClient.setQueryData(
        ["configuration", updatedEntity.key],
        updatedEntity,
      );
    },
    onError: (error) => {
      toast({
        title: error?.response?.data ?? "Error",
        status: "error",
        id: `update-toast-configuration-${key}`,
      });
    },
  });

  return {
    configuration,
    update,
    getAll,
    updateConfig,
  };
}

export const useTimezones = () =>
  useQuery<string[], AxiosError<any>>({
    queryKey: ["timezones"],
    queryFn: async () => {
      const { data } = await axios.get<string[]>(
        "/api/configuration/time/timezones",
      );
      return data;
    },
    staleTime: 1000 * 60 * 60 * 24,
  });

export const useUsersSync = () => {
  const syncUsers = useMutation({
    mutationFn: async () => {
      const { data } = await axios.put("/api/integration/users");
      return data;
    },
  });
  return { syncUsers };
};

export interface IEmailTest {
  to: string;
  subject: string;
  body?: string;
}

export const useEmailTest = () => {
  const toast = useToast({
    status: "success",
    duration: 2000,
    isClosable: true,
    position: "top",
  });

  return useMutation<unknown, AxiosError<any>, IEmailTest, any>({
    mutationFn: async (emailTest: IEmailTest) => {
      const { data } = await axios.put(`${endpoint}/smtp/test`, emailTest);
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Email sent",
        status: "success",
      });
    },
  });
};
